<template>
  <BuilderWrapper
    v-if="showOnDataVisualisationOrConsolidatedCustomVisualisation"
    helpful-tip="OrbViz automatically generates a unique page title and description which are displayed in search engine results and social media. These are generated based upon your level names and stories, application name and organisation name."
    data-test="page-title-description-settings"
  >
    <template slot="title">
      Page title and description
      <BetaBadge class="mr-1" />
    </template>

    <LabelledInput
      label="Page Title"
      helpful-tip="This is the title for the current page"
    >
      <b-input
        :value="pageTitle"
        disabled
      />
    </LabelledInput>

    <LabelledInput
      label="Page Description"
      helpful-tip="This is the description for the current page"
      :state="!longDescription"
      invalid-feedback="This description exceeds the recommended 160 character limit and may be truncated"
      data-cy="title-description-input"
    >
      <b-form-textarea
        :value="pageDescription"
        disabled
      />
    </LabelledInput>

    <LabelledInput
      label="Custom content"
      helpful-tip="If you prefer, you can customise the page title and/or description below. This follows the same inheritance model as the story content."
    >
      <b-checkbox
        v-model="customPageTitle"
        data-test="checkbox-custom-page-title"
      >
        Customise page title
      </b-checkbox>
      <b-checkbox
        v-model="customPageDescription"
        data-test="checkbox-custom-page-description"
      >
        Customise page description
      </b-checkbox>
    </LabelledInput>

    <template v-if="isVisualisationApp">
      <ActivatePathStoryPicker v-if="customPageTitle || customPageDescription" />

      <LabelledInput
        v-if="customPageTitle"
        label="Custom Page Title"
        data-test="labelled-input-custom-title"
      >
        <TTField
          v-if="showEditor"
          :panel="CUSTOM_TITLE"
          editor-height="4.4em"
        />
      </LabelledInput>

      <LabelledInput
        v-if="customPageDescription"
        label="Custom Page Description"
        data-test="labelled-input-custom-description"
      >
        <TTField
          v-if="showEditor"
          :panel="CUSTOM_DESCRIPTION"
          editor-height="12em"
        />
      </LabelledInput>
    </template>
    <GlobalPageTitleDescriptionSettings v-else />
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import BetaBadge from '@/components/builder/UI/BetaBadge.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import ActivatePathStoryPicker from '@/components/builder/StoryEditor/ActivatePathStoryPicker.vue'
import TTField from '@/components/TipTap/wrappers/TTField.vue'
import GlobalPageTitleDescriptionSettings from '@/components/builder/GlobalPageTitleDescriptionSettings.vue'
import { APP_CONFIG_KEY } from '@/constants'
import { AppConfig, StoryKeys } from '@/types'

const components = {
  BuilderWrapper,
  LabelledInput,
  BetaBadge,
  ActivatePathStoryPicker,
  TTField,
  GlobalPageTitleDescriptionSettings,
}

// eslint-disable-next-line no-use-before-define
@Component<TitleDescriptionSettings>({
  components,
  watch: {
    storyPath () {
      this.updateEditor()
    },
    storyBuilderPanel () {
      this.updateEditor()
    },
    defaultStoryPathToEdit () {
      this.updateEditor()
    },
  },
})
export default class TitleDescriptionSettings extends Vue {
  showEditor = true
  CUSTOM_TITLE = StoryKeys.CUSTOM_TITLE
  CUSTOM_DESCRIPTION = StoryKeys.CUSTOM_DESCRIPTION

  get isCustom () {
    return this.$typedStore.activeVisualisation.isCustom
  }

  get isVisualisationApp () {
    return this.$typedStore.primary.app.isVisualisationApp
  }

  get isConsolidatedApp () {
    return this.$typedStore.primary.app.isConsolidatedApp
  }

  get showOnConsolidatedCustomVisualisationsOnly () {
    return this.isConsolidatedApp && this.isCustom
  }

  get showOnDataVisualisationOrConsolidatedCustomVisualisation () {
    return this.showOnConsolidatedCustomVisualisationsOnly || this.isVisualisationApp
  }

  get customPageTitle () {
    return this.$typedStore.primary.app.config.appConfig.customPageTitle ?? false
  }

  set customPageTitle (value) {
    this.updateConfig(value, 'customPageTitle')
  }

  get customPageDescription () {
    return this.$typedStore.primary.app.config.appConfig.customPageDescription ?? false
  }

  set customPageDescription (value) {
    this.updateConfig(value, 'customPageDescription')
  }

  get pageTitle () {
    return this.$typedStore.primary.app.pageTitle
  }

  get pageDescription () {
    return this.$typedStore.primary.app.pageDescription
  }

  get storyBuilderPanel () {
    return this.$typedStore.public.display.storyBuilderPanel
  }

  get storyPath () {
    return this.$typedStore.public.display.storyPath
  }

  get defaultStoryPathToEdit () {
    return this.$typedStore.public.display.defaultStoryPathToEdit
  }

  get longDescription () {
    return this.pageDescription.length > 160
  }

  updateEditor () {
    this.showEditor = false
    this.$nextTick(() => { this.showEditor = true })
  }

  updateConfig (value: boolean, ...args: [keyof Pick<AppConfig, 'customPageTitle' | 'customPageDescription'>]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, ...args],
      value,
    })
  }
}

</script>
